import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { FiltersState } from './FilterPanel';
import { useGradeBands, useLanguages, useCurricula } from '@/hooks/useValueCatalog';
import { useExamCatalog } from '@/hooks/useExamCatalog';
import { currencySymbol } from '@/lib/currency';

const AVAILABILITY_LABELS: Record<string, string> = {
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

const SORT_LABELS: Record<string, string> = {
  PRICE_LOW: 'Price: Low to High',
  PRICE_HIGH: 'Price: High to Low',
  EXPERIENCE: 'Sort: Experience',
  START_DATE: 'Soonest start',
};

export interface Chip {
  key: string;
  label: string;
  onRemove: () => void;
}

interface ActiveFilterChipsProps {
  filters: FiltersState;
  onFiltersChange: (patch: Partial<FiltersState>) => void;
  /** Display-only — server-resolved (student profile or geo header), never user-editable */
  currency?: string;
  /**
   * Chips for selections that live outside FiltersState (e.g. teaching mode, topics,
   * and the per-type GROUP/CRASH filters), built by the caller. Appended after the
   * FiltersState chips so every applied filter shows here, per the FilterPanel design.
   */
  extraChips?: Chip[];
}

export function ActiveFilterChips({ filters, onFiltersChange, currency: displayCurrency, extraChips = [] }: ActiveFilterChipsProps) {
  const gradeBands = useGradeBands();
  const languages = useLanguages();
  const curricula = useCurricula();
  const { exams: targetExams } = useExamCatalog();

  const chips: Chip[] = [];

  if (filters.sortBy && filters.sortBy !== 'RELEVANCE') {
    chips.push({
      key: 'sortBy',
      label: SORT_LABELS[filters.sortBy] ?? filters.sortBy,
      onRemove: () => onFiltersChange({ sortBy: 'RELEVANCE' }),
    });
  }

  if (filters.offeringType && filters.offeringType !== 'ALL') {
    const offeringTypeLabel =
      filters.offeringType === 'ONE_TO_ONE' ? 'One-to-One' :
      filters.offeringType === 'GROUP' ? 'Group' :
      filters.offeringType === 'CRASH_COURSE' ? 'Crash Course' :
      filters.offeringType;
    chips.push({
      key: 'offeringType',
      label: offeringTypeLabel,
      onRemove: () => onFiltersChange({ offeringType: 'ALL' }),
    });
  }

  filters.gradeBand.forEach(gb => {
    chips.push({
      key: `gradeBand-${gb}`,
      label: gradeBands.labelOf(gb),
      onRemove: () =>
        onFiltersChange({ gradeBand: filters.gradeBand.filter(v => v !== gb) }),
    });
  });

  filters.language.forEach(lang => {
    chips.push({
      key: `lang-${lang}`,
      label: languages.labelOf(lang),
      onRemove: () =>
        onFiltersChange({ language: filters.language.filter(v => v !== lang) }),
    });
  });

  filters.curriculum.forEach(c => {
    chips.push({
      key: `curr-${c}`,
      label: curricula.labelOf(c),
      onRemove: () =>
        onFiltersChange({ curriculum: filters.curriculum.filter(v => v !== c) }),
    });
  });

  if (filters.availabilityTimeOfDay) {
    chips.push({
      key: 'availability',
      label: AVAILABILITY_LABELS[filters.availabilityTimeOfDay] ?? filters.availabilityTimeOfDay,
      onRemove: () => onFiltersChange({ availabilityTimeOfDay: '' }),
    });
  }

  if (filters.trialSessionAvailable === true) {
    chips.push({
      key: 'trial',
      label: 'Free trial',
      onRemove: () => onFiltersChange({ trialSessionAvailable: null }),
    });
  }

  if (filters.targetExamId) {
    chips.push({
      key: 'exam',
      label: targetExams.find(e => e.id === filters.targetExamId)?.name ?? filters.targetExamId,
      onRemove: () => onFiltersChange({ targetExamId: '' }),
    });
  }

  if (filters.topicKeywords) {
    chips.push({
      key: 'topic',
      label: `"${filters.topicKeywords}"`,
      onRemove: () => onFiltersChange({ topicKeywords: '' }),
    });
  }

  if (filters.priceMin != null || filters.priceMax != null) {
    const min = filters.priceMin ?? 0;
    const max = filters.priceMax;
    // SCRUM-365: backend currency symbol only — no 'USD' fallback.
    const sym = currencySymbol(displayCurrency);
    const label = max != null
      ? `${sym}${min.toLocaleString()} – ${sym}${max.toLocaleString()}`
      : `${sym}${min.toLocaleString()}+`;
    chips.push({
      key: 'price',
      label,
      onRemove: () => onFiltersChange({ priceMin: null, priceMax: null }),
    });
  }

  const allChips = [...chips, ...extraChips];

  if (allChips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {allChips.map(chip => (
        <Badge
          key={chip.key}
          variant="secondary"
          className="flex items-center gap-1 pr-1.5 cursor-default"
        >
          <span className="text-xs">{chip.label}</span>
          <button
            onClick={chip.onRemove}
            className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5 cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label={`Remove ${chip.label} filter`}
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}
